// Locutions — fixed multi-word phrases that behave as a single part of speech
// (muitas vezes = "often", apesar de = "in spite of"). Learners should memorise
// them whole. The 30 most common sit in a reference table at the bottom.
import { renderGuideSections } from "../ui/guideSections.js";

const COMMON = [
  ["muitas vezes", "often", "Vem cá muitas vezes."],
  ["às vezes", "sometimes", "Às vezes chove."],
  ["de vez em quando", "from time to time", "Saio de vez em quando."],
  ["com certeza", "of course", "Com certeza que sim."],
  ["no entanto", "however", "É caro; no entanto, compro-o."],
  ["portanto", "therefore", "Chove, portanto fico em casa."],
  ["se calhar", "maybe", "Se calhar vem."],
  ["de repente", "suddenly", "De repente foi-se embora."],
  ["em seguida", "right away / next", "Volto em seguida."],
  ["apesar de", "in spite of", "Apesar de tudo, sorri."],
  ["por fim", "finally", "Por fim chegaste!"],
  ["quanto a", "regarding", "Quanto a isso, não sei."],
  ["a partir de", "starting from", "A partir de hoje."],
  ["através de", "through / across", "Através da janela."],
  ["no final", "in the end", "No final conseguiu."],
  ["por exemplo", "for example", "Frutas, por exemplo, maçãs."],
  ["isto é", "that is to say", "Amanhã, isto é, segunda."],
  ["ou seja", "in other words", "Ou seja, não queres."],
  ["de facto", "in fact", "De facto, já sabia."],
  ["na realidade", "actually", "Na realidade não importa."],
  ["a propósito", "by the way", "A propósito, e a Ana?"],
  ["de novo", "again", "Tenta de novo."],
  ["já não", "no longer", "Já não vive aqui."],
  ["ainda não", "not yet", "Ainda não chegou."],
  ["pelo menos", "at least", "Pelo menos tentaste."],
  ["sobretudo", "above all", "Gosto, sobretudo do fim."],
  ["em vez de", "instead of", "Chá em vez de café."],
  ["ao longo de", "throughout", "Ao longo do dia."],
  ["no mínimo", "at least", "No mínimo três."],
  ["enfim", "anyway / in short", "Enfim, vamos embora."],
];

export function locutionsView(app) {
  const table = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">30 Most Common Locutions</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Portuguese</th><th class="py-2 px-3">English</th><th class="py-2 px-3">Example</th>
        </tr></thead>
        <tbody>
          ${COMMON
            .map(
              ([pt, en, ex]) => `<tr class="border-b border-sage-50 last:border-0">
            <td class="py-2 pr-3 font-medium text-sage-800">${pt}</td>
            <td class="py-2 px-3 text-sage-700">${en}</td>
            <td class="py-2 px-3 text-sage-500 italic">${ex}</td>
          </tr>`
            )
            .join("")}
        </tbody>
      </table></div>
    </div>`;

  renderGuideSections(
    app,
    "Locutions",
    "A locution (locução) is a fixed group of words that works as a single unit — one adverb, one preposition, or one conjunction made of several words. Because the meaning lives in the whole phrase, you learn them as set pieces, not word by word.",
    [
      {
        title: "1. What counts as a locution",
        body: `
          <p class="text-sage-700 mb-2">If you can't swap or remove a word without breaking the meaning, you're looking at a locution. <i>Apesar de</i> means "in spite of" as a block — none of <i>a</i>, <i>pesar</i>, or <i>de</i> carries that meaning alone.</p>
          <p class="text-sage-700 text-sm">They're the connective tissue of fluent Portuguese: dropping a few into your speech (<i>no entanto</i>, <i>de facto</i>, <i>portanto</i>) instantly makes it sound more natural.</p>
        `,
      },
      {
        title: "2. The main types",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>Adverbial</b> (act like an adverb): <i>muitas vezes</i> (often), <i>de repente</i> (suddenly), <i>em seguida</i> (right away).</li>
            <li><b>Prepositional</b> (act like a preposition): <i>apesar de</i> (in spite of), <i>através de</i> (through), <i>em vez de</i> (instead of).</li>
            <li><b>Conjunctive</b> (link clauses): <i>no entanto</i> (however), <i>portanto</i> (therefore), <i>isto é</i> (that is).</li>
            <li><b>Verbal</b> (a verb + fixed words): <i>aperceber-se de</i> (to realise), <i>ter em conta</i> (to bear in mind), <i>ter saudades de</i> (to miss).</li>
          </ul>
        `,
      },
      {
        title: "3. Using them as connectors",
        body: `
          <p class="text-sage-700 mb-2">Connective locutions usually open or pivot a sentence and are set off with commas:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>Estava cansado. <b>No entanto</b>, terminou o trabalho.</i></li>
            <li><i>Não tenho tempo; <b>portanto</b>, não posso ir.</i></li>
            <li><i><b>De facto</b>, já o tinha lido.</i></li>
          </ul>
          <p class="text-sage-700 text-sm mt-3">Mind the close pairs: <i>ou seja</i> ≈ <i>isto é</i> (rephrasing), while <i>se calhar</i> ≈ <i>talvez</i> (maybe) — but <i>talvez</i> usually takes the subjunctive, whereas <i>se calhar</i> takes the indicative.</p>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
